import { queryOptions, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { apiPaths } from "@/config/paths";
import { authApi } from "@/lib/api-client";
import type { QueryConfig } from "@/lib/react-query";
import { buildCountryOptions } from "@/lib/sign-up-countries";
import type { CountriesMap } from "@/lib/sign-up-countries";

export async function fetchSignUpCountries(): Promise<CountriesMap> {
  const { data } = await authApi.get<CountriesMap>(apiPaths.auth.countries.getPath());
  return data;
}

export const getSignUpCountriesQueryOptions = () =>
  queryOptions({
    queryKey: ["sign-up-countries"],
    queryFn: () => fetchSignUpCountries(),
    staleTime: 1000 * 60 * 60 * 24,
  });

type UseSignUpCountriesOptions = {
  queryConfig?: QueryConfig<typeof getSignUpCountriesQueryOptions>;
  showFirst?: string[];
};

export function useSignUpCountries({ queryConfig, showFirst }: UseSignUpCountriesOptions = {}) {
  const query = useQuery({
    ...getSignUpCountriesQueryOptions(),
    ...queryConfig,
  });

  const countryOptions = useMemo(
    () => (query.data ? buildCountryOptions(query.data, showFirst ?? []) : []),
    [query.data, showFirst]
  );

  return {
    ...query,
    countriesMap: query.data,
    countryOptions,
  };
}
